import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import { toast as alert } from "react-toastify";
import BookkeepingService from "../../../../services/BookkeepingService";
import WComponent from "../../../common/WComponent";
import ToolsSelect from "../kit/ToolsSelect";
import "./coa-mapping-review.css";

const MATCH_METHOD_LABELS = { gifi_lookup: "GIFI", keyword_exact: "Keyword", fuzzy_match: "Fuzzy", ai: "AI" };

class CoaMappingReview extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			suggestions: [],
			taxonomy: [],
			overrides: {},
			editing: {},
			saving: false,
		};
	}

	componentDidMount = async () => {
		await this.loadSuggestions();
	};

	loadSuggestions = async () => {
		const { clientId, accounts } = this.props;
		await this.update({ loading: true });
		const result = await BookkeepingService.mapCoaSuggest({ clientId, accounts });
		if (result) {
			await this.update({ suggestions: result.suggestions || [], taxonomy: result.taxonomy || [] });
		}
		await this.update({ loading: false });
	};

	getEffectiveMapping = (suggestion, index) => {
		const { overrides } = this.state;
		if (overrides[index]) return overrides[index];
		if (suggestion.suggested_canonical_code) {
			return {
				canonical_code: suggestion.suggested_canonical_code,
				display_name: suggestion.suggested_display_name,
			};
		}
		return null;
	};

	getTaxonomyChoices = () =>
		this.state.taxonomy.map(node => ({ value: node.canonical_code, label: node.display_name }));

	onOverrideByCode = (index, canonicalCode) => {
		const node = this.state.taxonomy.find(taxonomyNode => taxonomyNode.canonical_code === canonicalCode);
		if (node) this.onOverride(index, { canonical_code: node.canonical_code, display_name: node.display_name });
	};

	getMatchMethodLabel = (suggestion, isOverridden) => {
		if (isOverridden) return "Manual";
		return MATCH_METHOD_LABELS[suggestion.match_method] || "—";
	};

	onOverride = (index, item) => {
		this.setState(prev => {
			const editing = { ...prev.editing };
			delete editing[index];
			return {
				overrides: { ...prev.overrides, [index]: { canonical_code: item.canonical_code, display_name: item.display_name } },
				editing,
			};
		});
	};

	onStartEditing = index => {
		this.setState(prev => ({ editing: { ...prev.editing, [index]: true } }));
	};

	onConfirmAll = async () => {
		const { clientId, source = "caseware_import", onComplete } = this.props;
		const { suggestions } = this.state;

		await this.update({ saving: true });

		const mappings = suggestions
			.map((suggestion, index) => {
				const effective = this.getEffectiveMapping(suggestion, index);
				if (!effective) return null;
				return {
					accountNumber: suggestion.accountNumber || "",
					accountName: suggestion.accountName || suggestion.description || "",
					canonical_code: effective.canonical_code,
					gifiCode: suggestion.suggested_gifi_code || null,
					source,
				};
			})
			.filter(Boolean);

		const result = await BookkeepingService.mapCoaSave({ clientId, mappings });
		await this.update({ saving: false });

		if (result && result.saved > 0) {
			onComplete({ mappings });
		} else {
			alert.error("Failed to save the mappings — check your connection and try again.");
		}
	};

	renderSuggestionRow = (suggestion, index) => {
		const { overrides, editing } = this.state;
		const effective = this.getEffectiveMapping(suggestion, index);
		const isOverridden = !!overrides[index];
		const isUnmatched = !effective;
		const confidence = isOverridden ? 100 : Math.round((suggestion.confidence || 0) * 100);

		return (
			<div key={index} className={clsx("coa-review__row", isUnmatched && "coa-review__row--unmatched")}>
				<div className="coa-review__col-account">
					<div className="coa-review__account-number">{suggestion.accountNumber}</div>
					<div className="coa-review__account-name">{suggestion.accountName || suggestion.description}</div>
				</div>
				<div className="coa-review__col-arrow">
					<Icon.ArrowRight size={14} className={clsx("coa-review__arrow", effective && "coa-review__arrow--on")} />
				</div>
				<div className="coa-review__col-match">
					{(isUnmatched || editing[index]) ? (
						<ToolsSelect
							value=""
							options={this.getTaxonomyChoices()}
							onChange={canonicalCode => this.onOverrideByCode(index, canonicalCode)}
							placeholder="Search FS accounts…"
							searchable
							searchPlaceholder="Search FS accounts…"
						/>
					) : (
						<span className={clsx("coa-review__match-name", isOverridden && "coa-review__match-name--overridden")}>
							{effective.display_name}
						</span>
					)}
					{effective && !editing[index] && (
						<button className="coa-review__change-btn" onClick={() => this.onStartEditing(index)}>
							Change
						</button>
					)}
				</div>
				<div className="coa-review__col-confidence">
					{effective && (
						<span className={clsx(
							"coa-review__confidence",
							confidence >= 90 && "coa-review__confidence--high",
							confidence >= 70 && confidence < 90 && "coa-review__confidence--medium",
							confidence < 70 && "coa-review__confidence--low",
						)}>
							{confidence}%
						</span>
					)}
				</div>
				<div className="coa-review__col-method">
					{this.getMatchMethodLabel(suggestion, isOverridden)}
				</div>
			</div>
		);
	};

	render() {
		const { title, subtitle, hideSkip = false, onSkip } = this.props;
		const { loading, suggestions, saving } = this.state;

		if (loading) {
			return <div className="coa-review__loading">Matching accounts to FS accounts...</div>;
		}

		const matchedCount = suggestions.filter((suggestion, index) => this.getEffectiveMapping(suggestion, index)).length;
		const unmatchedCount = suggestions.length - matchedCount;

		return (
			<div className="coa-review">
				<div className="coa-review__header">
					<h3 className="coa-review__title">{title || "Map Your Chart of Accounts"}</h3>
					<p className="coa-review__subtitle">
						{subtitle || (
							<>
								The system matched {matchedCount} of {suggestions.length} accounts automatically.
								{unmatchedCount > 0 && ` Review the ${unmatchedCount} unmatched account${unmatchedCount > 1 ? "s" : ""} below.`}
							</>
						)}
					</p>
				</div>

				<div className="coa-review__stats">
					<span className="coa-review__stat coa-review__stat--matched">
						<Icon.CheckCircle size={14} /> {matchedCount} matched
					</span>
					{unmatchedCount > 0 && (
						<span className="coa-review__stat coa-review__stat--unmatched">
							<Icon.AlertCircle size={14} /> {unmatchedCount} need review
						</span>
					)}
				</div>

				<div className="coa-review__table">
					<div className="coa-review__table-header">
						<div className="coa-review__col-account">Your Account</div>
						<div className="coa-review__col-arrow" />
						<div className="coa-review__col-match">FS Account</div>
						<div className="coa-review__col-confidence">Confidence</div>
						<div className="coa-review__col-method">Method</div>
					</div>

					{suggestions.map(this.renderSuggestionRow)}
				</div>

				<div className="coa-review__footer">
					{!hideSkip ? (
						<button type="button" className="tk-button tk-button--secondary" onClick={onSkip}>Skip — use default accounts</button>
					) : (
						<span />
					)}
					<button type="button" className="tk-button tk-button--primary" onClick={this.onConfirmAll} disabled={matchedCount === 0 || saving}>
						{saving ? "Saving…" : `Confirm ${matchedCount} mappings`}
					</button>
				</div>
			</div>
		);
	}
}

export default CoaMappingReview;
